import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Paper,
  Skeleton,
  Typography
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useEffect, useRef } from "react";

import ColorChip from "../../../components/ColorChip";
import MarkdownRenderer from "../../../components/MarkdownRenderer";
import RatingIndicator from "../../../components/RatingIndicator";
import { ViewerArtifacts } from "../../../data/queries/viewer";
import { isUnscoredStatus } from "../../../types/artifact";
interface PerformanceTabProps {
  data: ViewerArtifacts | null;
  isLoading: boolean;
  expandedAttribute?: string | null;
  onAttributeChange?: (attributeName: string | null) => void;
}

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant="subtitle1"
    fontWeight="bold"
    color="primary"
    sx={{ mb: 1, mt: 2 }}
  >
    {children}
  </Typography>
);

const AttributeAccordion = ({
  attribute,
  expanded = false,
  accordionRef,
  onExpand
}: {
  attribute: any;
  expanded?: boolean;
  accordionRef?: React.RefObject<HTMLDivElement | null>;
  onExpand?: (attributeName: string, isExpanded: boolean) => void;
}) => {
  const handleChange = (_event: React.SyntheticEvent, isExpanded: boolean) => {
    if (onExpand) {
      onExpand(attribute.attribute, isExpanded);
    }
  };

  return (
    <Accordion expanded={expanded} onChange={handleChange} ref={accordionRef}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Grid container spacing={2} sx={{ width: "100%" }} >
          <Grid size={{ xs: 12, md: 2 }} sx={{ width: 200, minWidth: 200 }}>
            <RatingIndicator rating={attribute.score} />
          </Grid>
          <Grid >
            <Typography variant="body2" fontWeight="bold">
              {attribute.attribute}
            </Typography>
          </Grid>
        </Grid>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="body2">
          {attribute.summary}
        </Typography>
        {attribute.next_time && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" fontWeight="bold" sx={{ mt: 1 }}>
              Next time:
            </Typography>
            <Typography variant="body2">
              {attribute.next_time}
            </Typography>
          </Box>
        )}
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" fontWeight="bold" sx={{ mt: 1 }}>
            Supporting Details:
          </Typography>
          <ul style={{ paddingLeft: "1.5rem", margin: "0.5rem 0" }}>
            {attribute.details.map((detail: string, index: number) => (
              <li key={index}>
                <Typography variant="body2">{detail}</Typography>
              </li>
            ))}
          </ul>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export const PerformanceTab = ({
  data,
  isLoading,
  expandedAttribute,
  onAttributeChange
}: PerformanceTabProps) => {
  const expandedAccordionRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (expandedAttribute && expandedAccordionRef.current && !isLoading) {
      const timeoutId = setTimeout(() => {
        expandedAccordionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center"
        });
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [expandedAttribute, isLoading]);

  const handleAttributeExpand = (attributeName: string, isExpanded: boolean) => {
    if (onAttributeChange) {
      onAttributeChange(isExpanded ? attributeName : null);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ width: "100%" }}>
        <Skeleton variant="text" sx={{ fontSize: "2rem" }} />
        <Skeleton variant="rectangular" height={120} />
        <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
        <Skeleton variant="rectangular" height={200} sx={{ mt: 2 }} />
        <Skeleton variant="rectangular" height={200} sx={{ mt: 2 }} />
      </Box>
    );
  }

  const scorecard = data?.scorecard?.content?.scorecard;

  if (!scorecard) {
    return (
      <Box>
        <Paper elevation={0} sx={{ p: 0, mb: 2 }}>
          <Typography variant="body1" color="text.secondary">
            No scorecard data available for this interaction.
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (!scorecard?.analysis || isUnscoredStatus(scorecard?.call_suitability?.status)) {
    return (
      <Box>
        <Paper elevation={0} sx={{ p: 0, mb: 2 }}>
          <Typography variant="body1" color="text.secondary">
            This call has not been scored.
          </Typography>

          {scorecard.call_suitability?.reason_description && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Reason:
              </Typography>
              <Typography variant="body1">
                {scorecard.call_suitability.reason_description}
              </Typography>
            </Box>
          )}

          {scorecard.protocol_name && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Protocol:
              </Typography>
              <Typography variant="body1">
                {scorecard.protocol_name}
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Paper elevation={0} sx={{ p: 0, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 8 }}>
            <SectionTitle>{scorecard.protocol_name}</SectionTitle>
            <Typography variant="body1" paragraph>
              {scorecard.call_summary}
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Paper elevation={1} sx={{ p: 2, bgcolor: "background.default" }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Overall Score
                </Typography>

                <ColorChip value={scorecard.overall_score} field="score" />

              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="body1">
                {scorecard.call_suitability.reason_description}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <SectionTitle>Attribute Scores</SectionTitle>
        <Box sx={{ mt: 2 }}>
          {scorecard.analysis.map((attribute: any, index: number) => {
            const isExpanded = expandedAttribute === attribute.attribute;
            return (
              <AttributeAccordion
                key={index}
                attribute={attribute}
                expanded={isExpanded}
                accordionRef={isExpanded ? expandedAccordionRef : undefined}
                onExpand={handleAttributeExpand}
              />
            );
          })}
        </Box>

        {scorecard.evaluation_process && (
          <Box sx={{ mt: 3 }}>
            <SectionTitle>Evaluation Process</SectionTitle>
            <Paper elevation={1} sx={{ p: 2, bgcolor: "background.default" }}>
              <MarkdownRenderer content={scorecard.evaluation_process} />
            </Paper>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default PerformanceTab;
